import Cookies from 'js-cookie';
import { CacheKey } from '../types/cache-keys';
import { getCookie } from './cookieHelper';
import { beginSessionRecovery } from './slices/uiSlice';
import { store } from './store';
import { queryClient } from './queryClient';

const api = process.env.REACT_APP_API;

// Env vars are strings, so a bare truthiness test treats "false" as on.
const mockApi = (process.env.REACT_APP_MOCK_SF_API ?? '').toLowerCase() === 'true';

/**
 * A rejected token (401) means the session expired mid-use. The old behaviour reset the
 * Redux user slice and did `window.location.href = '/'`, which flipped signinState to
 * LoggedOut and dropped the user onto the login screen, losing whatever page and context
 * they were on. Instead we enter session recovery over the current page: signinState stays
 * LoggedIn and the page stays mounted while `useAuthenticateUser` first attempts a silent
 * re-auth (backend cookie session restore) and, only if that fails, raises the blocking login
 * overlay. Either way postLoginPath (captured here) returns the user to where they were.
 *
 * Once recovery has begun we stop reacting to further 401s. Every in-flight request that was
 * carrying the rejected token lands here on its own rejection; without the guard they would
 * each re-dispatch on every retry. The full-page reload that used to end that burst is gone,
 * so the guard takes its place. A full-page re-auth redirect reloads the app and clears the
 * guard naturally; resetReauthGuard() covers both the in-app (mock) path that does not reload
 * and a successful silent re-auth, after which later expiries must be able to recover again.
 */
let reauthInFlight = false;

export const resetReauthGuard = () => {
  reauthInFlight = false;
};

// When this module is first evaluated the app is (re)loading. redux-persist does NOT persist the
// toolbox JWT (store.ts blacklists it for XSS safety), so on every reload the in-memory session
// starts token-less and `useAuthenticateUser`'s silent restore re-mints it from the backend cookie
// session. During that window the app has already fired its initial authenticated queries against
// whatever cookie token exists (possibly a short-lived one about to be replaced) — so a 401 here is
// a bootstrap race, NOT a mid-session expiry, and must not kick off the disruptive recovery redirect
// that reads to the user as "reloading logs me out immediately".
const APP_LOADED_AT = Date.now();
const RECOVERY_BOOTSTRAP_GRACE_MS = 12_000;

// A Cisco SSO user never has a Salesforce session, so a 401 from sf/* means "no Salesforce session",
// which re-running Cisco SSO cannot fix and which would otherwise loop.
const isUnfixableSalesforce401 = (endpoint?: string) =>
  !!endpoint &&
  normalizeEndpoint(endpoint).startsWith('sf/') &&
  (store.getState() as any)?.user?.authProvider === 'cisco_sso';

const refreshOn401 = async (endpoint?: string) => {
  if (isUnfixableSalesforce401(endpoint)) return;

  // A 401 before the user ever had a token (e.g. on the login screen) is not an expiry to
  // recover in place — there is no session to re-establish, so leave it to the login flow.
  if (!getToolboxToken()) return;

  // Bootstrap grace: within the first few seconds of a page load, before the silent restore has
  // rehydrated the toolbox JWT into redux, a 401 is a hydration race — not a genuine expiry. Skip
  // recovery so the reload can finish establishing the session. This is scoped tightly: it applies
  // ONLY while redux still holds no toolbox token AND we are inside the load window. In normal
  // operation the token is present in redux, so a real mid-session 401 recovers exactly as before;
  // and once the window elapses, a still-dead session recovers on the next 401 too.
  const reduxToolboxToken = (store.getState() as any)?.user?.toolboxToken;
  if (!reduxToolboxToken && Date.now() - APP_LOADED_AT < RECOVERY_BOOTSTRAP_GRACE_MS) return;

  if (reauthInFlight) return;
  reauthInFlight = true;

  queryClient.removeQueries({ queryKey: [CacheKey.User] });

  // Remember exactly where the user is so the re-auth round-trip returns them here rather
  // than the app root. Both OAuth callbacks consume postLoginPath on success.
  const here = window.location.pathname + window.location.search;
  if (here && here !== '/') {
    localStorage.setItem('postLoginPath', here);
  }

  // Deliberately no reset() and no window.location redirect: the tokens stay in place (the
  // server rejects them anyway, and the overlay blocks interaction) so the app stays
  // LoggedIn and the user's page stays mounted behind the overlay while recovery runs.
  store.dispatch(beginSessionRecovery());
};

const normalizeEndpoint = (endpoint: string) => {
  let p = endpoint || '';

  try {
    if (p.startsWith('http://') || p.startsWith('https://')) {
      p = new URL(p).pathname;
    }
  } catch {
    // ignore normalization failure
  }

  p = p.replace(/^\/+/, '');

  if (p.startsWith('api/')) {
    p = p.slice(4);
  }

  return p;
};

/**
 * A cookie under the branch-prefixed name this app writes, or the plain name.
 *
 * `getCookie` already applies the prefix, so passing it a prefixed name would look up
 * `dev_dev_tb_jwt` and never match. The plain read is the one that matters: a token set
 * by the backend or another tool does not carry our branch prefix.
 */
const getBranchOrPlainCookie = (name: string) => getCookie(name) || Cookies.get(name);

/**
 * Whether a JWT's own exp claim has already passed.
 *
 * This never accepts a token - the signature is the server's business - it only skips one
 * that cannot possibly be accepted. A value that does not parse is reported as not expired,
 * because the Salesforce token is not a JWT and must still flow through the selectors below.
 *
 * It exists because `toolboxToken` is persisted by redux-persist and is read *before* any
 * cookie: a token the backend has refused is rehydrated on every reload, so one 401 becomes
 * permanent until site data is cleared by hand. That was invisible on the deployed origin,
 * where refreshOn401 cleared the session, and fatal on localhost, where its old
 * REACT_APP_SALESFORCE_REFRESH_URL guard made the whole function a no-op.
 */
export const isExpiredJwt = (token: string) => {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return false;
  }

  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));

    return typeof payload?.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
};

/** The first candidate that is present and not a spent JWT. */
const firstUsableToken = (candidates: (string | null | undefined)[]) => {
  for (const candidate of candidates) {
    if (candidate && !isExpiredJwt(candidate)) {
      return candidate;
    }
  }

  return null;
};

const getToolboxToken = () => {
  const state: any = store.getState();

  return firstUsableToken([
    state?.user?.toolboxToken,
    getBranchOrPlainCookie('tb_jwt'),
    getBranchOrPlainCookie('toolbox_jwt'),
    getBranchOrPlainCookie('tb_token'),
    getBranchOrPlainCookie('toolbox_token'),
    getBranchOrPlainCookie('toolboxToken'),
  ]);
};

const getSalesforceToken = () => {
  const state: any = store.getState();

  return state?.user?.sfToken || getBranchOrPlainCookie('sf_token') || null;
};

const isToolboxTokenEndpoint = (endpoint: string) => {
  const normalized = normalizeEndpoint(endpoint);

  return (
    normalized.startsWith('sf/') ||
    normalized.startsWith('db/') ||
    normalized.startsWith('ai/') ||
    normalized.startsWith('queue/') ||
    normalized.startsWith('jira-cloud/') ||
    normalized.startsWith('moodle/') ||
    normalized.startsWith('dcloud/generate-token/') ||

    normalized.startsWith('auth/client/') ||
    normalized === 'auth/client' ||
    normalized.startsWith('auth/authz/') ||
    normalized.startsWith('auth/authorize/') ||
    normalized.startsWith('auth/token/validate') ||
    normalized.startsWith('auth/token/revoke') ||
    normalized.startsWith('auth/logout') ||
    normalized.startsWith('auth/session/validate')
  );
};

const isAuthBootstrapEndpoint = (endpoint?: string) => {
  const normalized = normalizeEndpoint(endpoint || '');

  return (
    normalized.startsWith('auth/token/') &&
    !normalized.startsWith('auth/token/validate') &&
    !normalized.startsWith('auth/token/revoke')
  ) || (
    normalized.startsWith('auth/oauth/_callback') ||
    normalized.startsWith('auth/cisco/token')
  );
};

const getApiHeaders = (
  token: string | null = null,
  method = 'GET',
  endpoint?: string
) => {
  const toolboxToken = getToolboxToken();
  const salesforceToken = getSalesforceToken();

  let effectiveToken = token;
  let effectiveTokenKind: 'toolbox' | 'salesforce' | 'explicit' | null = null;

  if (effectiveToken) {
    effectiveTokenKind = 'explicit';
  } else if (endpoint && isToolboxTokenEndpoint(endpoint)) {
    effectiveToken = toolboxToken || salesforceToken;
    effectiveTokenKind = toolboxToken ? 'toolbox' : salesforceToken ? 'salesforce' : null;
  } else if (endpoint && isAuthBootstrapEndpoint(endpoint)) {
    effectiveToken = token || null;
    effectiveTokenKind = token ? 'explicit' : null;
  } else {
    effectiveToken = toolboxToken || salesforceToken;
    effectiveTokenKind = toolboxToken ? 'toolbox' : salesforceToken ? 'salesforce' : null;
  }

  if (mockApi && process.env.NODE_ENV === 'development') {
    return {
      'Content-Type': 'application/json',
      'react-app-mock-sf-api': 'true',
      Origin: 'http://localhost:3001',
      ...(effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {}),
      ...(effectiveTokenKind === 'toolbox'
        ? { 'X-Toolbox-Authorization': `Bearer ${effectiveToken}` }
        : {}),
      ...(effectiveTokenKind === 'salesforce'
        ? { 'X-Salesforce-Authorization': `Bearer ${effectiveToken}` }
        : {}),
    };
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };

  if (effectiveToken) {
    headers.Authorization = `Bearer ${effectiveToken}`;

    if (effectiveTokenKind === 'toolbox') {
      headers['X-Toolbox-Authorization'] = `Bearer ${effectiveToken}`;
    }

    if (effectiveTokenKind === 'salesforce') {
      headers['X-Salesforce-Authorization'] = `Bearer ${effectiveToken}`;
    }

    // Both tokens, when we hold both. The backend tries the Toolbox JWT first and, when it
    // fails to verify, falls through to the Salesforce token rather than rejecting - see
    // SalesforceAuthMiddleware / authn.Middleware.Handler. Sending only whichever token won
    // the selection above defeated that: a deployed browser survives a JWT failure on its
    // sf_token *cookie*, but these requests use credentials: 'same-origin', so cross-origin
    // local development sends no cookies at all and the fallback had nothing to read. This
    // costs nothing when the JWT verifies - the header is never looked at in that case.
    if (effectiveTokenKind === 'toolbox' && salesforceToken) {
      headers['X-Salesforce-Authorization'] = `Bearer ${salesforceToken}`;
    }
  }

  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
    const csrfToken = Cookies.get('csrftoken');

    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
  }

  return headers;
};

const withTrailingSlash = (path: string) => (path.endsWith('/') ? path : `${path}/`);

const buildQueryParams = (params: Record<string, any>) => {
  const query = new URLSearchParams();

  for (const key in params) {
    if (params[key] !== undefined && params[key] !== null) {
      query.append(key, params[key]);
    }
  }

  return query.toString();
};

const buildApiUrl = (
  endpoint: string,
  params: Record<string, any> = {},
  forceTrailingSlash = false,
) => {
  const baseUrl = api;
  const normalized = normalizeEndpoint(endpoint);
  const path = forceTrailingSlash ? withTrailingSlash(normalized) : normalized;

  const queryParams = buildQueryParams(params);

  return queryParams ? `${baseUrl}/${path}?${queryParams}` : `${baseUrl}/${path}`;
};

export {
  buildApiUrl,
  buildQueryParams,
  getApiHeaders,
  refreshOn401,
  normalizeEndpoint,
};